/**
 * PDFToolbar - Unified header toolbar component
 *
 * Extracted from PDFViewerModal.tsx
 */

import React from 'react';
import {
  ZoomIn,
  ZoomOut,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Scissors,
  Image as ImageIcon,
  List,
  BookmarkPlus,
  Bookmark,
  RotateCcw,
  RotateCw,
  XCircle,
  Maximize,
  Scroll,
  Download,
  ExternalLink,
  X,
} from 'lucide-react';
import type { Theme } from '../../../themes/theme';
import type { FitMode, OutlineItem, Bookmark as BookmarkType } from '../types/pdf-viewer.types';

export interface PDFToolbarProps {
  theme: Theme;
  documentName: string;
  searchText: string;
  textMatches: any[];
  scale: number;
  fitMode: FitMode;
  showFitDropdown: boolean;
  fitDropdownPosition: { top: number; right: number } | null;
  fitButtonRef: React.RefObject<HTMLButtonElement>;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitModeChange: (mode: FitMode) => void;
  onShowFitDropdown: (show: boolean) => void;
  onFitDropdownPositionChange: (pos: { top: number; right: number } | null) => void;
  pageNumber: number;
  numPages: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  captureMode: boolean;
  onCaptureModeToggle: () => void;
  goToPreviousMatch: () => void;
  goToNextMatch: () => void;
  showThumbnails: boolean;
  onShowThumbnails: (show: boolean) => void;
  outline: OutlineItem[] | null;
  showOutline: boolean;
  onShowOutline: (show: boolean) => void;
  onAddBookmark: () => void;
  bookmarks: BookmarkType[];
  showBookmarksPanel: boolean;
  onShowBookmarksPanel: (show: boolean) => void;
  rotation: number;
  onRotateCounterClockwise: () => void;
  onRotateClockwise: () => void;
  onResetRotation: () => void;
  presentationMode: boolean;
  onPresentationModeToggle: () => void;
  scrollMode: 'continuous' | 'page-by-page';
  onScrollModeToggle: () => void;
  onDownload: () => void;
  onOpenInNewTab: () => void;
  embedded: boolean;
  onClosePanel?: () => void;
  onClose: () => void;
}

export function PDFToolbar({
  theme,
  documentName,
  searchText,
  textMatches,
  scale,
  fitMode,
  showFitDropdown,
  fitDropdownPosition,
  fitButtonRef,
  onZoomIn,
  onZoomOut,
  onFitModeChange,
  onShowFitDropdown,
  onFitDropdownPositionChange,
  pageNumber,
  numPages,
  onPreviousPage,
  onNextPage,
  captureMode,
  onCaptureModeToggle,
  goToPreviousMatch,
  goToNextMatch,
  showThumbnails,
  onShowThumbnails,
  outline,
  showOutline,
  onShowOutline,
  onAddBookmark,
  bookmarks,
  showBookmarksPanel,
  onShowBookmarksPanel,
  rotation,
  onRotateCounterClockwise,
  onRotateClockwise,
  onResetRotation,
  presentationMode,
  onPresentationModeToggle,
  scrollMode,
  onScrollModeToggle,
  onDownload,
  onOpenInNewTab,
  embedded,
  onClosePanel,
  onClose,
}: PDFToolbarProps) {
  return (
    <div
      className={`border-b ${theme.colors.border.primary} ${theme.colors.bg.secondary} flex items-center justify-between px-4 py-1.5 gap-3 flex-wrap flex-shrink-0 max-h-[5vh] overflow-hidden`}
    >
      {/* Título */}
      <div className="flex-1 min-w-0 mr-2">
        <h2 className={`text-xs font-medium ${theme.colors.text.primary} truncate leading-tight`}>
          {documentName}
        </h2>
        {searchText && textMatches.length > 0 && (
          <p className={`text-[10px] ${theme.colors.text.primary} leading-tight`}>
            {textMatches.length} coincidencia{textMatches.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Controles de Zoom y Ajuste */}
      <div className="flex items-center gap-1">
        <button
          onClick={onZoomOut}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Alejar"
        >
          <ZoomOut size={16} />
        </button>
        <span
          className={`text-sm ${theme.colors.text.secondary} min-w-[45px] text-center font-medium`}
        >
          {Math.round(scale * 100)}%
        </span>
        <button
          onClick={onZoomIn}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Acercar"
        >
          <ZoomIn size={16} />
        </button>
        <div className="h-4 w-px bg-nb-border mx-1" />
        <div className="relative">
          <button
            ref={fitButtonRef}
            onClick={() => {
              if (fitButtonRef.current) {
                const rect = fitButtonRef.current.getBoundingClientRect();
                onFitDropdownPositionChange({
                  top: rect.bottom + 4,
                  right: window.innerWidth - rect.right,
                });
              }
              onShowFitDropdown(!showFitDropdown);
            }}
            className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${
              theme.colors.state.hover
            } rounded transition-colors ${
              fitMode !== 'manual' ? `${theme.colors.bg.tertiary}` : ''
            }`}
            title="Modos de ajuste"
          >
            <Maximize2 size={16} />
          </button>
          {showFitDropdown && fitDropdownPosition && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => {
                  onShowFitDropdown(false);
                  onFitDropdownPositionChange(null);
                }}
              />
              <div
                className="fixed rounded shadow-lg p-1 z-[100] min-w-[140px]"
                style={{
                  top: `${fitDropdownPosition.top}px`,
                  right: `${fitDropdownPosition.right}px`,
                  backgroundColor: theme.colors.bg.primary,
                  borderColor: theme.colors.border.primary,
                  borderWidth: '1px',
                  borderStyle: 'solid',
                }}
              >
                <button
                  onClick={() => {
                    onFitModeChange('width');
                    onShowFitDropdown(false);
                    onFitDropdownPositionChange(null);
                  }}
                  className="block w-full text-left px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: fitMode === 'width' ? theme.colors.bg.tertiary : 'transparent',
                    color: theme.colors.text.primary,
                  }}
                >
                  Ajustar a ancho
                </button>
                <button
                  onClick={() => {
                    onFitModeChange('height');
                    onShowFitDropdown(false);
                    onFitDropdownPositionChange(null);
                  }}
                  className="block w-full text-left px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor:
                      fitMode === 'height' ? theme.colors.bg.tertiary : 'transparent',
                    color: theme.colors.text.primary,
                  }}
                >
                  Ajustar a altura
                </button>
                <button
                  onClick={() => {
                    onFitModeChange('page');
                    onShowFitDropdown(false);
                    onFitDropdownPositionChange(null);
                  }}
                  className="block w-full text-left px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor: fitMode === 'page' ? theme.colors.bg.tertiary : 'transparent',
                    color: theme.colors.text.primary,
                  }}
                >
                  Ajustar a página
                </button>
                <button
                  onClick={() => {
                    onFitModeChange('manual');
                    onShowFitDropdown(false);
                    onFitDropdownPositionChange(null);
                  }}
                  className="block w-full text-left px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity"
                  style={{
                    backgroundColor:
                      fitMode === 'manual' ? theme.colors.bg.tertiary : 'transparent',
                    color: theme.colors.text.primary,
                  }}
                >
                  Manual
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Navegación de Páginas */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={onPreviousPage}
          disabled={pageNumber <= 1}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Página anterior"
        >
          <ChevronLeft size={16} />
        </button>

        <span
          className={`text-sm ${theme.colors.text.primary} min-w-[70px] text-center font-medium`}
        >
          {pageNumber}/{numPages}
        </span>

        <button
          onClick={onNextPage}
          disabled={pageNumber >= numPages}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          title="Página siguiente"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Recortar snippet - PRIORITARIO, siempre visible */}
      <div className="flex items-center gap-1">
        <div className="h-4 w-px bg-nb-border mx-1" />
        <button
          onClick={onCaptureModeToggle}
          className={`p-1.5 ${
            captureMode
              ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
              : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
          } rounded transition-colors`}
          title={captureMode ? 'Cancelar captura' : 'Recortar snippet'}
        >
          <Scissors size={16} />
        </button>
      </div>

      {/* Navegación de Coincidencias (solo si hay búsqueda activa) */}
      {searchText && textMatches.length > 0 && (
        <div className="flex items-center gap-1">
          <button
            onClick={goToPreviousMatch}
            className={`p-1.5 ${theme.colors.text.primary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
            title="Coincidencia anterior"
          >
            <ChevronLeft size={16} />
          </button>

          <span
            className={`text-sm ${theme.colors.text.primary} min-w-[50px] text-center font-medium`}
          >
            {textMatches.length > 0
              ? `${textMatches.findIndex((m: any) => m.pageNumber === pageNumber) + 1 || 1}/${
                  textMatches.length
                }`
              : '0/0'}
          </span>

          <button
            onClick={goToNextMatch}
            className={`p-1.5 ${theme.colors.text.primary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
            title="Coincidencia siguiente"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Botones de Navegación y Herramientas */}
      <div className="flex items-center gap-1">
        {/* Botones de navegación lateral */}
        <button
          onClick={() => onShowThumbnails(!showThumbnails)}
          className={`p-1.5 ${
            showThumbnails
              ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
              : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
          } rounded transition-colors`}
          title="Mostrar miniaturas"
        >
          <ImageIcon size={16} />
        </button>

        {outline && outline.length > 0 && (
          <button
            onClick={() => onShowOutline(!showOutline)}
            className={`p-1.5 ${
              showOutline
                ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
                : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
            } rounded transition-colors`}
            title="Mostrar índice"
          >
            <List size={16} />
          </button>
        )}

        <button
          onClick={onAddBookmark}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Agregar marcador"
        >
          <BookmarkPlus size={16} />
        </button>

        {bookmarks.length > 0 && (
          <button
            onClick={() => onShowBookmarksPanel(!showBookmarksPanel)}
            className={`p-1.5 ${
              showBookmarksPanel
                ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
                : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
            } rounded transition-colors`}
            title="Mostrar marcadores"
          >
            <Bookmark size={16} />
          </button>
        )}

        <div className="h-4 w-px bg-nb-border mx-1" />

        {/* Rotación */}
        <button
          onClick={onRotateCounterClockwise}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Rotar 90° antihorario"
        >
          <RotateCcw size={16} />
        </button>

        <button
          onClick={onRotateClockwise}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Rotar 90° horario"
        >
          <RotateCw size={16} />
        </button>

        {rotation !== 0 && (
          <button
            onClick={onResetRotation}
            className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
            title="Restablecer rotación"
          >
            <XCircle size={16} />
          </button>
        )}

        <div className="h-4 w-px bg-nb-border mx-1" />

        {/* Modo presentación y acciones */}
        <button
          onClick={onPresentationModeToggle}
          className={`p-1.5 ${
            presentationMode
              ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
              : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
          } rounded transition-colors`}
          title="Modo presentación"
        >
          <Maximize size={16} />
        </button>

        <button
          onClick={onScrollModeToggle}
          className={`p-1.5 ${
            scrollMode === 'page-by-page'
              ? `${theme.colors.bg.tertiary} ${theme.colors.text.inverse}`
              : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover}`
          } rounded transition-colors`}
          title={
            scrollMode === 'page-by-page'
              ? 'Modo página por página (activado)'
              : 'Modo continuo (activado)'
          }
        >
          <Scroll size={16} />
        </button>

        <button
          onClick={onDownload}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Descargar PDF"
        >
          <Download size={16} />
        </button>

        <button
          onClick={onOpenInNewTab}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Abrir en nueva pestaña"
        >
          <ExternalLink size={16} />
        </button>

        <button
          onClick={embedded && onClosePanel ? onClosePanel : onClose}
          className={`p-1.5 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
          title="Cerrar"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
